import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import L from 'leaflet';
import { Layers, MapPin, Search, ZoomIn, ZoomOut, Maximize2, RotateCcw, Check, Info, X, ChevronRight, Truck, Clock, DollarSign, Package } from 'lucide-react';
import { createTileLayer } from '../utils/mapTiles';

// Color interpolation helpers
function interpolateColor(color1, color2, factor) {
  const c1 = parseInt(color1.slice(1), 16);
  const c2 = parseInt(color2.slice(1), 16);

  const r1 = (c1 >> 16) & 255;
  const g1 = (c1 >> 8) & 255;
  const b1 = c1 & 255;

  const r2 = (c2 >> 16) & 255;
  const g2 = (c2 >> 8) & 255;
  const b2 = c2 & 255;

  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

// Multi-step gradient for density/volume metrics (Cyan -> Blue -> Purple -> Emerald)
function getGradientColor(t) {
  const factor = Math.max(0, Math.min(1, t));
  if (factor < 0.33) {
    return interpolateColor('#38bdf8', '#6366f1', factor / 0.33);
  } else if (factor < 0.66) {
    return interpolateColor('#6366f1', '#a855f7', (factor - 0.33) / 0.33);
  } else {
    return interpolateColor('#a855f7', '#22c55e', (factor - 0.66) / 0.34);
  }
}

// Rate gradient (Red -> Amber -> Green)
function getRateColor(ratePct) {
  const r = Math.max(0, Math.min(100, ratePct));
  if (r < 75) {
    return interpolateColor('#ef4444', '#f59e0b', r / 75);
  } else {
    return interpolateColor('#f59e0b', '#22c55e', (r - 75) / 25);
  }
}

const palette = [
  '#38bdf8', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#6366f1',
  '#14b8a6', '#eab308', '#ec4899', '#8b5cf6', '#84cc16', '#f97316'
];

export const npaMetricOptions = [
  { value: 'stopsCount', label: 'Densité d\'arrêts (nb stops)', unit: 'arrêts' },
  { value: 'weight', label: 'Poids total (kg)', unit: 'kg' },
  { value: 'volume', label: 'Volume total (m³)', unit: 'm³' },
  { value: 'revenue', label: 'Chiffre d\'affaires (CHF)', unit: 'CHF' },
  { value: 'onTimeRate', label: 'Taux de ponctualité (% à l\'heure)', unit: '%' },
  { value: 'successRate', label: 'Taux de succès (% livré)', unit: '%' },
  { value: 'avgServiceTime', label: 'Temps de service moyen (min)', unit: 'min' },
  { value: 'none', label: 'Neutre (Sans nuance)' }
];

export const clientDotColorOptions = [
  { value: 'status', label: 'Par statut de livraison' },
  { value: 'on_time', label: 'Par ponctualité (À l\'heure vs Retard)' },
  { value: 'type', label: 'Par type (Livraison vs Ramasse)' },
  { value: 'round', label: 'Par tournée' },
  { value: 'revenue', label: 'Par chiffre d\'affaires' },
  { value: 'weight', label: 'Par poids' }
];

export const clientDotSizeOptions = [
  { value: 'fixed', label: 'Taille standard (fixe)' },
  { value: 'weight', label: 'Selon le poids (kg)' },
  { value: 'volume', label: 'Selon le volume (m³)' },
  { value: 'revenue', label: 'Selon le CA (CHF)' },
  { value: 'service_time', label: 'Selon le temps de service (min)' }
];

export default function SwissZipsMap({
  stops = [],
  clients = [],
  hubs = [],
  rounds = [],
  onSelectStop
}) {
  const mapContainerRef = useRef(null);
  const leafletMapRef = useRef(null);
  const geoJsonLayerRef = useRef(null);
  const clientMarkersLayerRef = useRef(null);
  const hubMarkersLayerRef = useRef(null);
  const zipLayersRef = useRef({}); // zipCode -> Array of L.Path

  const [geoJsonData, setGeoJsonData] = useState(null);
  const [isLoadingGeoJson, setIsLoadingGeoJson] = useState(false);
  const [geoJsonError, setGeoJsonError] = useState(null);

  // Map Controls State
  const [npaMetric, setNpaMetric] = useState('stopsCount');
  const [clientDotColor, setClientDotColor] = useState('on_time');
  const [clientDotSize, setClientDotSize] = useState('weight');

  const [showZips, setShowZips] = useState(true);
  const [showClients, setShowClients] = useState(true);
  const [showHubs, setShowHubs] = useState(true);

  const [selectedZip, setSelectedZip] = useState(null);
  const [hoveredZip, setHoveredZip] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Map of unique rounds to colors
  const roundColorMap = useMemo(() => {
    const map = {};
    rounds.forEach((r, idx) => {
      map[r.name || r.id] = palette[idx % palette.length];
    });
    return map;
  }, [rounds]);

  // Aggregate stats per Swiss ZIP code from stops data
  const zipStatsMap = useMemo(() => {
    const map = {};
    stops.forEach(s => {
      const zip = String(s.postal_code || '').trim();
      if (!zip) return;

      if (!map[zip]) {
        map[zip] = {
          zip,
          city: s.city || '',
          stopsCount: 0,
          clientsSet: new Set(),
          weight: 0,
          volume: 0,
          revenue: 0,
          onTimeCount: 0,
          deliveredCount: 0,
          failedCount: 0,
          totalServiceTimeMins: 0,
          totalDelayMins: 0,
          stopsList: []
        };
      }

      const z = map[zip];
      z.stopsCount += 1;
      if (s.client_name) z.clientsSet.add(s.client_name);
      z.weight += (s.weightKg || s.weight_kg || 0);
      z.volume += (s.volumeM3 || s.volume_m3 || 0);
      z.revenue += (s.revenue || 0);
      if (s.isOnTime) z.onTimeCount += 1;
      if (s.isDelivered) z.deliveredCount += 1;
      if (s.isFailed) z.failedCount += 1;
      z.totalServiceTimeMins += (s.serviceTimeMins || 10);
      z.totalDelayMins += (s.twDelayMins || 0);
      z.stopsList.push(s);
    });

    // Compute derived rates
    Object.values(map).forEach(z => {
      z.onTimeRate = z.stopsCount > 0 ? Math.round((z.onTimeCount / z.stopsCount) * 100) : 0;
      z.successRate = z.stopsCount > 0 ? Math.round((z.deliveredCount / z.stopsCount) * 100) : 0;
      z.avgServiceTime = z.stopsCount > 0 ? Math.round((z.totalServiceTimeMins / z.stopsCount) * 10) / 10 : 0;
      z.clientsCount = z.clientsSet.size;
      z.weight = Math.round(z.weight * 10) / 10;
      z.volume = Math.round(z.volume * 10) / 10;
      z.revenue = Math.round(z.revenue * 10) / 10;
    });

    return map;
  }, [stops]);

  // Max stats for normalization in choropleth scale
  const maxStats = useMemo(() => {
    const zips = Object.values(zipStatsMap);
    if (zips.length === 0) return { stopsCount: 1, weight: 1, volume: 1, revenue: 1, avgServiceTime: 1 };
    return {
      stopsCount: Math.max(...zips.map(z => z.stopsCount), 1),
      weight: Math.max(...zips.map(z => z.weight), 1),
      volume: Math.max(...zips.map(z => z.volume), 1),
      revenue: Math.max(...zips.map(z => z.revenue), 1),
      avgServiceTime: Math.max(...zips.map(z => z.avgServiceTime), 1)
    };
  }, [zipStatsMap]);

  // Max stats for Client Dots
  const maxClientStats = useMemo(() => {
    if (stops.length === 0) return { weight: 1000, volume: 10, revenue: 500, serviceTime: 30 };
    return {
      weight: Math.max(...stops.map(s => parseFloat(s.weightKg || s.weight_kg || 0)), 100),
      volume: Math.max(...stops.map(s => parseFloat(s.volumeM3 || s.volume_m3 || 0)), 1),
      revenue: Math.max(...stops.map(s => parseFloat(s.revenue || 0)), 100),
      serviceTime: Math.max(...stops.map(s => parseInt(s.serviceTimeMins || 10, 10)), 15)
    };
  }, [stops]);

  // Fetch Swiss ZIP codes GeoJSON
  useEffect(() => {
    let isMounted = true;
    setIsLoadingGeoJson(true);
    fetch('/swiss-zip-codes.geojson')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (isMounted) {
          setGeoJsonData(data);
          setIsLoadingGeoJson(false);
        }
      })
      .catch(err => {
        console.error('Error loading swiss-zip-codes.geojson:', err);
        if (isMounted) {
          setGeoJsonError('Impossible de charger les polygones des NPA suisses.');
          setIsLoadingGeoJson(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize Leaflet Map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    if (!leafletMapRef.current) {
      const map = L.map(mapContainerRef.current, {
        preferCanvas: true, // Canvas mode for ~4,000 Swiss polygons
        zoomSnap: 0.25,
        zoomDelta: 0.5,
        wheelPxPerZoomLevel: 100,
        center: [46.8182, 8.2275],
        zoom: 8.5,
        zoomControl: false
      });

      // Dark Theme OpenStreetMap Basemap
      createTileLayer('dark').addTo(map);

      // Layer groups
      clientMarkersLayerRef.current = L.layerGroup().addTo(map);
      hubMarkersLayerRef.current = L.layerGroup().addTo(map);

      leafletMapRef.current = map;
    }

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);

  // Dynamic Polygon Styler for ZIP codes
  const getPolygonStyle = useCallback((feature) => {
    const zipCode = String(feature?.properties?.ZIP4 || '').trim();
    const stats = zipStatsMap[zipCode];
    const isSelected = selectedZip === zipCode;
    const isHovered = hoveredZip === zipCode;

    if (isSelected) {
      return {
        fillColor: '#38bdf8',
        fillOpacity: 0.85,
        color: '#ffffff',
        weight: 2.5,
        opacity: 1
      };
    }

    if (isHovered) {
      return {
        fillColor: '#f59e0b',
        fillOpacity: 0.80,
        color: '#f59e0b',
        weight: 2.5,
        opacity: 1
      };
    }

    // If no stops recorded in this ZIP code
    if (!stats || stats.stopsCount === 0) {
      return {
        fillColor: '#1e293b',
        fillOpacity: 0.25,
        color: '#334155',
        weight: 0.6,
        opacity: 0.45
      };
    }

    // Color by selected NPA metric
    let fillColor = '#38bdf8';
    let fillOpacity = 0.65;

    if (npaMetric === 'none') {
      fillColor = '#38bdf8';
      fillOpacity = 0.45;
    } else if (npaMetric === 'onTimeRate') {
      fillColor = getRateColor(stats.onTimeRate);
      fillOpacity = 0.70;
    } else if (npaMetric === 'successRate') {
      fillColor = getRateColor(stats.successRate);
      fillOpacity = 0.70;
    } else {
      const maxVal = maxStats[npaMetric] || 1;
      const curVal = stats[npaMetric] || 0;
      const t = Math.min(1, Math.max(0.15, curVal / maxVal));
      fillColor = getGradientColor(t);
      fillOpacity = 0.4 + t * 0.45;
    }

    return {
      fillColor,
      fillOpacity,
      color: '#0f172a',
      weight: 1.2,
      opacity: 0.85
    };
  }, [zipStatsMap, selectedZip, hoveredZip, npaMetric, maxStats]);

  // Update styles of all polygons belonging to a given ZIP code
  const updateZipPolygonStyle = useCallback((zipCode) => {
    const layers = zipLayersRef.current[zipCode];
    if (layers && Array.isArray(layers)) {
      layers.forEach(layer => {
        if (layer && typeof layer.setStyle === 'function') {
          layer.setStyle(getPolygonStyle(layer.feature));
        }
      });
    }
  }, [getPolygonStyle]);

  // Render or Update GeoJSON Layer
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !geoJsonData) return;

    if (geoJsonLayerRef.current) {
      map.removeLayer(geoJsonLayerRef.current);
      geoJsonLayerRef.current = null;
    }

    if (!showZips) return;

    zipLayersRef.current = {};

    const geoLayer = L.geoJSON(geoJsonData, {
      style: getPolygonStyle,
      onEachFeature: (feature, layer) => {
        const zipCode = String(feature?.properties?.ZIP4 || '').trim();
        if (!zipCode) return;

        if (!zipLayersRef.current[zipCode]) {
          zipLayersRef.current[zipCode] = [];
        }
        zipLayersRef.current[zipCode].push(layer);

        // Hover events
        layer.on('mouseover', () => {
          setHoveredZip(zipCode);
          const group = zipLayersRef.current[zipCode];
          if (group) {
            group.forEach(l => {
              if (selectedZip !== zipCode) {
                l.setStyle({ color: '#f59e0b', weight: 2.5, fillOpacity: 0.85, opacity: 1 });
              }
            });
          }
        });

        layer.on('mouseout', () => {
          setHoveredZip(null);
          updateZipPolygonStyle(zipCode);
        });

        // Click selection
        layer.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          setSelectedZip(prev => prev === zipCode ? null : zipCode);
        });
      }
    });

    geoLayer.addTo(map);
    geoJsonLayerRef.current = geoLayer;

    // Attach dynamic tooltips
    Object.keys(zipLayersRef.current).forEach(zipCode => {
      const stats = zipStatsMap[zipCode];
      const cityName = stats?.city || '';
      const tooltipContent = `
        <div style="font-family:Inter, sans-serif; font-size:11px; padding:3px 6px; color:#f8fafc; background:#0f172a; border-radius:6px; border:1px solid #334155;">
          <div style="font-weight:800; font-size:12px; color:#38bdf8;">NPA ${zipCode} ${cityName ? `· ${cityName}` : ''}</div>
          ${stats ? `
            <div style="margin-top:4px; display:grid; grid-template-columns:1fr 1fr; gap:4px; font-size:10px;">
              <div>Arrêts: <b>${stats.stopsCount}</b> (${stats.clientsCount} clients)</div>
              <div>Ponctualité: <b style="color:${stats.onTimeRate >= 90 ? '#22c55e' : '#ef4444'};">${stats.onTimeRate}%</b></div>
              <div>Poids: <b>${stats.weight} kg</b></div>
              <div>CA: <b>${stats.revenue} CHF</b></div>
            </div>
          ` : '<div style="color:#94a3b8; font-size:10px; margin-top:2px;">Aucun arrêt planifié</div>'}
        </div>
      `;

      zipLayersRef.current[zipCode].forEach(l => {
        l.bindTooltip(tooltipContent, { sticky: true, direction: 'auto', opacity: 0.95 });
      });
    });

  }, [geoJsonData, showZips, getPolygonStyle, updateZipPolygonStyle, zipStatsMap, selectedZip]);

  // Update styles when metric or selection changes without reloading whole GeoJSON
  useEffect(() => {
    if (!geoJsonLayerRef.current) return;
    Object.keys(zipLayersRef.current).forEach(zipCode => {
      updateZipPolygonStyle(zipCode);
    });
  }, [npaMetric, selectedZip, hoveredZip, updateZipPolygonStyle]);

  // Render Hub Markers
  useEffect(() => {
    const hubGroup = hubMarkersLayerRef.current;
    if (!hubGroup) return;

    hubGroup.clearLayers();
    if (!showHubs) return;

    hubs.forEach(h => {
      if (h.latitude && h.longitude) {
        const hubIcon = L.divIcon({
          className: 'custom-hub-map-icon',
          html: `
            <div style="
              background: #0f172a;
              color: #f59e0b;
              border: 2px solid #f59e0b;
              border-radius: 6px;
              width: 28px;
              height: 28px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 800;
              font-size: 10px;
              box-shadow: 0 4px 14px rgba(245, 158, 11, 0.4);
              transform: rotate(45deg);
            ">
              <span style="transform: rotate(-45deg);">HUB</span>
            </div>
          `,
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        });

        const marker = L.marker([h.latitude, h.longitude], { icon: hubIcon });
        marker.bindPopup(`
          <div style="font-family:Inter, sans-serif; font-size:12px; color:#0f172a; padding:4px;">
            <strong style="font-size:13px; color:#0f172a;">🏭 Dépôt / Hub : ${h.name}</strong><br/>
            <span style="color:#64748b; font-size:11px;">${h.address || ''}, ${h.city || ''} (${h.postal_code || ''})</span>
          </div>
        `);
        hubGroup.addLayer(marker);
      }
    });
  }, [hubs, showHubs]);

  // Render Client / Stop Dots (CircleMarkers)
  useEffect(() => {
    const clientGroup = clientMarkersLayerRef.current;
    if (!clientGroup) return;

    clientGroup.clearLayers();
    if (!showClients) return;

    stops.forEach((stop, idx) => {
      const lat = parseFloat(stop.latitude);
      const lng = parseFloat(stop.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      // Filter by selected ZIP if active
      if (selectedZip && String(stop.postal_code || '').trim() !== selectedZip) {
        return;
      }

      // Compute Dot Radius based on metric
      let radius = 7;
      if (clientDotSize === 'weight') {
        const w = parseFloat(stop.weightKg || stop.weight_kg || 0);
        radius = Math.min(22, Math.max(4, Math.round(4 + (w / (maxClientStats.weight || 1)) * 18)));
      } else if (clientDotSize === 'volume') {
        const v = parseFloat(stop.volumeM3 || stop.volume_m3 || 0);
        radius = Math.min(22, Math.max(4, Math.round(4 + (v / (maxClientStats.volume || 1)) * 18)));
      } else if (clientDotSize === 'revenue') {
        const rev = parseFloat(stop.revenue || 0);
        radius = Math.min(22, Math.max(4, Math.round(4 + (rev / (maxClientStats.revenue || 1)) * 18)));
      } else if (clientDotSize === 'service_time') {
        const st = parseInt(stop.serviceTimeMins || 10, 10);
        radius = Math.min(20, Math.max(4, Math.round(4 + (st / (maxClientStats.serviceTime || 1)) * 16)));
      }

      // Compute Dot Color based on metric
      let dotColor = '#38bdf8';
      if (clientDotColor === 'status') {
        if (stop.isDelivered) dotColor = '#22c55e';
        else if (stop.status === 'in_progress') dotColor = '#38bdf8';
        else if (stop.isFailed) dotColor = '#ef4444';
        else dotColor = '#f59e0b';
      } else if (clientDotColor === 'on_time') {
        dotColor = stop.isOnTime ? '#22c55e' : '#ef4444';
      } else if (clientDotColor === 'type') {
        dotColor = stop.type === 'pickup' ? '#f59e0b' : '#38bdf8';
      } else if (clientDotColor === 'round') {
        dotColor = roundColorMap[stop.round_name] || '#38bdf8';
      } else if (clientDotColor === 'revenue') {
        const t = Math.min(1, Math.max(0, (stop.revenue || 0) / (maxClientStats.revenue || 1)));
        dotColor = getGradientColor(t);
      } else if (clientDotColor === 'weight') {
        const t = Math.min(1, Math.max(0, (stop.weightKg || stop.weight_kg || 0) / (maxClientStats.weight || 1)));
        dotColor = getGradientColor(t);
      }

      const circle = L.circleMarker([lat, lng], {
        radius,
        fillColor: dotColor,
        fillOpacity: 0.85,
        color: '#0f172a',
        weight: 1.5,
        pane: 'markerPane'
      });

      // Tooltip on Hover
      circle.bindTooltip(`
        <div style="font-family:Inter, sans-serif; font-size:11px; padding:2px 4px; color:#f8fafc; background:#0f172a; border-radius:4px; border:1px solid #334155;">
          <b>${stop.client_name || 'Client'}</b> (${stop.postal_code || ''} ${stop.city || ''})<br/>
          <span>${stop.type === 'pickup' ? '📦 Ramasse' : '🚚 Livraison'} · ${stop.weightKg || stop.weight_kg || 0} kg · ${stop.revenue || 0} CHF</span>
        </div>
      `, { sticky: true });

      // Click Popup
      circle.bindPopup(`
        <div style="font-family:Inter, sans-serif; font-size:12px; color:#0f172a; padding:6px; min-width:200px;">
          <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:4px;">
            <strong style="font-size:13px; color:#0f172a;">${stop.client_name || 'Client'}</strong>
            <span style="font-size:10px; font-weight:800; padding:2px 6px; border-radius:4px; background:${stop.type === 'pickup' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(56, 189, 248, 0.15)'}; color:${stop.type === 'pickup' ? '#d97706' : '#0284c7'};">
              ${stop.type === 'pickup' ? 'RAMASSE' : 'LIVRAISON'}
            </span>
          </div>
          <div style="font-size:11px; color:#64748b; margin-bottom:6px;">
            ${stop.address || ''}, ${stop.postal_code || ''} ${stop.city || ''}
          </div>
          <div style="display:grid; grid-template-columns:1fr 1fr; gap:4px; font-size:11px; border-top:1px solid #e2e8f0; padding-top:6px;">
            <div>Poids: <b>${stop.weightKg || stop.weight_kg || 0} kg</b></div>
            <div>Volume: <b>${stop.volumeM3 || stop.volume_m3 || 0} m³</b></div>
            <div>CA: <b>${stop.revenue || 0} CHF</b></div>
            <div>Service: <b>${stop.serviceTimeMins || 10} min</b></div>
            <div>Arrivée: <b>${stop.arrivalTime || '--:--'}</b></div>
            <div>Ponctualité: <b style="color:${stop.isOnTime ? '#16a34a' : '#dc2626'};">${stop.isOnTime ? '✓ À l\'heure' : `✗ Retard (${stop.twDelayMins}m)`}</b></div>
          </div>
        </div>
      `);

      circle.on('click', () => {
        if (onSelectStop) onSelectStop(stop.id);
      });

      clientGroup.addLayer(circle);
    });
  }, [stops, selectedZip, showClients, clientDotColor, clientDotSize, maxClientStats, roundColorMap, onSelectStop]);

  // Fit bounds to active stops
  const handleFitToStops = () => {
    const map = leafletMapRef.current;
    if (!map) return;

    const validLatLngs = stops
      .map(s => [parseFloat(s.latitude), parseFloat(s.longitude)])
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));

    if (validLatLngs.length > 0) {
      const bounds = L.latLngBounds(validLatLngs);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  };

  // Fit bounds to Switzerland
  const handleFitToSwitzerland = () => {
    const map = leafletMapRef.current;
    if (!map) return;
    map.setView([46.8182, 8.2275], 8.5);
  };

  // Search & jump to ZIP
  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;

    // Check if it matches a ZIP code
    const layers = zipLayersRef.current[q];
    if (layers && layers.length > 0) {
      setSelectedZip(q);
      const group = L.featureGroup(layers);
      leafletMapRef.current?.fitBounds(group.getBounds(), { padding: [50, 50], maxZoom: 13 });
      return;
    }

    // Check if it matches a client name
    const matchedStop = stops.find(s =>
      String(s.client_name || '').toLowerCase().includes(q.toLowerCase()) ||
      String(s.city || '').toLowerCase().includes(q.toLowerCase())
    );

    if (matchedStop && matchedStop.latitude && matchedStop.longitude) {
      leafletMapRef.current?.setView([matchedStop.latitude, matchedStop.longitude], 14);
      if (matchedStop.postal_code) setSelectedZip(String(matchedStop.postal_code).trim());
    }
  };

  const selectedZipStats = selectedZip ? zipStatsMap[selectedZip] : null;

  return (
    <div style={{ position: 'relative', width: '100%', height: '620px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #334155', background: '#0f172a' }}>
      
      {/* MAP CANVAS CONTAINER */}
      <div ref={mapContainerRef} style={{ width: '100%', height: '100%', zIndex: 1 }} />

      {/* TOP FLOATING CONTROL BAR */}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        right: '12px',
        zIndex: 1000,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
        background: 'rgba(15, 23, 42, 0.92)',
        backdropFilter: 'blur(10px)',
        border: '1px solid #334155',
        borderRadius: '10px',
        padding: '8px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
      }}>
        {/* NPA COLOR METRIC */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#38bdf8' }}>Couleur NPA :</span>
          <select
            value={npaMetric}
            onChange={e => setNpaMetric(e.target.value)}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 8px', fontSize: '0.73rem', outline: 'none' }}
          >
            {npaMetricOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* CLIENT DOT COLOR */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#22c55e' }}>Couleur Points :</span>
          <select
            value={clientDotColor}
            onChange={e => setClientDotColor(e.target.value)}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 8px', fontSize: '0.73rem', outline: 'none' }}
          >
            {clientDotColorOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* CLIENT DOT SIZE */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#f59e0b' }}>Taille Points :</span>
          <select
            value={clientDotSize}
            onChange={e => setClientDotSize(e.target.value)}
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#f8fafc', borderRadius: '6px', padding: '4px 8px', fontSize: '0.73rem', outline: 'none' }}
          >
            {clientDotSizeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* LAYER TOGGLES */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={showZips} onChange={e => setShowZips(e.target.checked)} style={{ accentColor: '#38bdf8' }} />
            <span>NPA</span>
          </label>
          <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={showClients} onChange={e => setShowClients(e.target.checked)} style={{ accentColor: '#22c55e' }} />
            <span>Clients</span>
          </label>
          <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={showHubs} onChange={e => setShowHubs(e.target.checked)} style={{ accentColor: '#f59e0b' }} />
            <span>Hubs</span>
          </label>

          {/* QUICK SEARCH */}
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="text"
              placeholder="Chercher NPA, client..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ background: '#1e293b', border: '1px solid #334155', color: '#fff', padding: '3px 8px', borderRadius: '6px', fontSize: '0.72rem', width: '130px' }}
            />
            <button type="submit" style={{ background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '6px', padding: '4px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <Search size={12} />
            </button>
          </form>

          {/* ZOOM BUTTONS */}
          <button
            onClick={handleFitToStops}
            title="Centrer sur les arrêts"
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#38bdf8', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}
          >
            Arrêts
          </button>
          <button
            onClick={handleFitToSwitzerland}
            title="Recadrer sur la Suisse entière"
            style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', borderRadius: '6px', padding: '4px 8px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}
          >
            Suisse
          </button>
        </div>
      </div>

      {/* BOTTOM LEFT: COLOR SCALE LEGEND */}
      {showZips && npaMetric !== 'none' && (
        <div style={{
          position: 'absolute',
          bottom: '16px',
          left: '16px',
          zIndex: 1000,
          background: 'rgba(15, 23, 42, 0.90)',
          backdropFilter: 'blur(8px)',
          border: '1px solid #334155',
          borderRadius: '8px',
          padding: '8px 12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
          minWidth: '180px'
        }}>
          <div style={{ fontSize: '0.68rem', fontWeight: '800', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '6px' }}>
            Nuance NPA : {npaMetricOptions.find(o => o.value === npaMetric)?.label}
          </div>
          {npaMetric === 'onTimeRate' || npaMetric === 'successRate' ? (
            <div>
              <div style={{ height: '8px', borderRadius: '4px', background: 'linear-gradient(to right, #ef4444, #f59e0b, #22c55e)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.67rem', color: '#94a3b8', marginTop: '4px' }}>
                <span>0% (Critique)</span>
                <span>80%</span>
                <span>100% (Optimal)</span>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ height: '8px', borderRadius: '4px', background: 'linear-gradient(to right, #38bdf8, #6366f1, #a855f7, #22c55e)' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.67rem', color: '#94a3b8', marginTop: '4px' }}>
                <span>Faible (0)</span>
                <span>Moyen</span>
                <span>Élevé (Max {maxStats[npaMetric] || 1})</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* RIGHT SIDE: SELECTED NPA INSPECTOR CARD */}
      {selectedZip && (
        <div style={{
          position: 'absolute',
          top: '70px',
          right: '16px',
          zIndex: 1000,
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(10px)',
          border: '1px solid #38bdf8',
          borderRadius: '10px',
          padding: '12px 14px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.6)',
          width: '280px',
          maxHeight: '480px',
          overflowY: 'auto'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid #334155', paddingBottom: '6px' }}>
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: '800', color: '#38bdf8' }}>
                NPA {selectedZip} {selectedZipStats?.city ? `· ${selectedZipStats.city}` : ''}
              </div>
              <div style={{ fontSize: '0.70rem', color: '#94a3b8' }}>
                {selectedZipStats ? `${selectedZipStats.stopsCount} arrêt(s) · ${selectedZipStats.clientsCount} client(s)` : 'Zone non desservie'}
              </div>
            </div>
            <button
              onClick={() => setSelectedZip(null)}
              style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '2px' }}
            >
              <X size={15} />
            </button>
          </div>

          {selectedZipStats ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginBottom: '10px' }}>
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px' }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Ponctualité</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '800', color: selectedZipStats.onTimeRate >= 90 ? '#22c55e' : '#ef4444' }}>
                    {selectedZipStats.onTimeRate}%
                  </div>
                </div>
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px' }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Poids Total</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f8fafc' }}>
                    {selectedZipStats.weight} kg
                  </div>
                </div>
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px' }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Chiffre d'Affaires</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#38bdf8' }}>
                    {selectedZipStats.revenue} CHF
                  </div>
                </div>
                <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '6px', padding: '6px' }}>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Temps Moyen</div>
                  <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#f59e0b' }}>
                    {selectedZipStats.avgServiceTime} min
                  </div>
                </div>
              </div>

              <div style={{ fontSize: '0.72rem', fontWeight: '700', color: '#f8fafc', marginBottom: '6px' }}>
                Arrêts dans ce NPA ({selectedZipStats.stopsList.length}) :
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {selectedZipStats.stopsList.map((s, idx) => (
                  <div
                    key={idx}
                    onClick={() => onSelectStop && onSelectStop(s.id)}
                    style={{
                      background: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '6px',
                      padding: '5px 8px',
                      fontSize: '0.70rem',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '700', color: '#f8fafc' }}>{s.client_name}</div>
                      <div style={{ color: '#94a3b8', fontSize: '0.65rem' }}>{s.type === 'pickup' ? 'Ramasse' : 'Livraison'} · {s.weightKg || s.weight_kg || 0} kg</div>
                    </div>
                    <span style={{ color: s.isOnTime ? '#22c55e' : '#ef4444', fontWeight: '800', fontSize: '0.68rem' }}>
                      {s.isOnTime ? '✓ À l\'heure' : `+${s.twDelayMins}m`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: '0.74rem', textAlign: 'center', padding: '12px 0' }}>
              Aucun arrêt dans cette zone pour la période sélectionnée.
            </div>
          )}
        </div>
      )}

      {/* LOADING OVERLAY */}
      {isLoadingGeoJson && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <div style={{ width: '32px', height: '32px', border: '3px solid #38bdf8', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: '0.8rem', color: '#f8fafc', fontWeight: '600' }}>Chargement des 4 000 zones NPA suisses...</span>
        </div>
      )}

    </div>
  );
}
